
// Pure math for PointViewTopFraming.js's "T" shortcut - kept separate from
// any THREE/DOM dependency so it stays Level 1 (no-browser) unit testable,
// same split this project already uses throughout (see e.g.
// digitization/geometry/localPlaneFit.js's own header comment for why).
//
//   preliminary octree hierarchy walk -> a tight-as-cheaply-possible XY/Z
//   extent -> a straight-down camera placement that frames exactly that
//   extent, honouring the viewport's own aspect ratio and a small margin
//
// This module never touches the live scene/camera itself - PointViewTopFraming.js
// owns reading `viewer.scene`/`viewer.renderer`/the active camera and
// committing the result back onto `viewer.scene.view`.

// ── Cheap XY(Z) extent from an already-resident octree hierarchy ───────────
//
// Walks `root.children` (a plain {0: node, 1: node, ...} object, same shape
// on every PointCloudOctreeGeometryNode - see PointCloudOctreeGeometry.js)
// and unions the LOCAL boundingBox of every visited LEAF node (no children
// of its own) whose numPoints > 0.
//
// Why this is safe to call synchronously on every "T" keypress, with no
// network/decode cost and no full point scan: for this project's own
// point clouds (the classic cloud.js/PotreeConverter format, confirmed via
// POCLoader.js's `version.upTo('1.4')` branch), the ENTIRE node hierarchy -
// every node's boundingBox and numPoints - is decoded synchronously from
// the single cloud.js response right when the point cloud is added to the
// scene, long before any point PAYLOAD is fetched (PointCloudOctreeGeometryNode's
// own `.loaded` flag tracks payload decoding separately and is irrelevant
// here - boundingBox/numPoints are already known regardless of `.loaded`).
// So this walk only touches metadata that is already sitting in memory -
// it triggers no `.load()`/hierarchy fetch of its own.
//
// LEAVES ONLY, deliberately - an earlier version of this function unioned
// EVERY populated node's box, ancestors included. Potree stores its own
// LOD subsample of points directly AT non-leaf (ancestor) nodes too, but an
// ancestor's box is a whole, unsubdivided octree CELL - much bigger than
// where its points actually sit relative to real density - so a
// high-numPoints ancestor (the root itself routinely holds thousands, for
// the coarsest LOD) could single-handedly prop the union open to its own
// full cell size regardless of outlier trimming below, since it usually
// has too much mass to ever be trimmed as an "outlier". A LEAF is the
// FINEST subdivision the converter actually found necessary for its own
// little region of space - measured directly on this project's own small
// dig-pit dataset, switching from all-levels to leaves-only tightened the
// resulting XY box by roughly 12-25% on each axis (a lone 108-point leaf
// sitting alone at one corner, invisible next to the >1,000,000 total
// leaf-level points, had been propping the X-max boundary open by itself
// under the old all-levels union). This is still purely a structural
// property of the octree itself (which cells got subdivided vs. stayed
// coarse) - no point positions are read, only which resident nodes are
// leaves.
//
// Ancestor-level mass is intentionally excluded from the weighted-centroid/
// outlier-trim calculation below too, for the same reason: geometrically,
// any point stored at an ancestor's own LOD level still lies within that
// ancestor's box, which is exactly the union of its own children's boxes -
// so the leaves under it already carry the same spatial signal, without
// the ancestor's own oversized box distorting the final extent.
//
// Keep the point-weighted closest 98% of the octree's own leaf-level point
// mass -
// see selectMainMassNodes() below. The excluded tail (at most 2% of total
// points, and only ever the FAR tail by weighted distance) is what "sparse,
// isolated outlier area" means here.
export const OUTLIER_KEEP_FRACTION = 0.98;

// Below this many populated nodes, a weighted centroid/distance ordering is
// too noisy to trust as "main mass vs. outlier tail" - just keep everything
// (matches this module's own general bias toward never over-trimming a
// small/clean dataset).
const MIN_NODES_FOR_TRIMMING = 8;

// Robust, point-mass-weighted node selection (task: "Vægt efter numPoints...
// noder med meget lav pointmasse og stor afstand fra hovedmassen kan
// ignoreres... men aldrig beskære egentlige sammenhængende dele"). Computes
// one weighted XY centroid (weight = numPoints, so a handful of stray
// low-mass nodes barely moves it - the centroid is already fairly robust by
// construction before any trimming happens), sorts every node by its
// distance from that centroid, and keeps nodes in that order until the
// running point-mass total reaches OUTLIER_KEEP_FRACTION of the grand
// total.
//
// Deliberately mass-fraction-based, NOT a fixed sigma/distance cutoff: a
// node (or a whole contiguous run of nodes) far from the centroid is still
// KEPT as long as it keeps contributing real mass along the way - its own
// weight keeps the running total climbing steadily as the distance-sorted
// sweep reaches it, same as any other substantial region. Only nodes whose
// combined weight is small enough to still be sitting in the unreached
// tail once the closer 98% has already been accounted for get dropped -
// exactly "low mass AND far", never "far" alone. This is what protects a
// large/elongated dataset (e.g. a long pipe trench) from being cropped by
// its own far end, while still discarding a handful of stray points sitting
// far off on their own with negligible mass.
//
// Caveat (documented rather than hidden): without true point-to-point
// connectivity, this can't distinguish "genuinely sparse but real feature"
// from "isolated noise" any more precisely than mass share allows - a real
// contiguous feature whose total mass share is itself below the excluded
// tail's share could, in principle, still be trimmed. OUTLIER_KEEP_FRACTION
// is deliberately conservative (98%) so this only ever bites the most
// marginal, lowest-mass fringe.
function selectMainMassNodes(nodes, keepFraction){
	if(nodes.length < MIN_NODES_FOR_TRIMMING) return nodes;

	let totalWeight = 0, sumX = 0, sumY = 0;
	for(const n of nodes){
		totalWeight += n.weight;
		sumX += n.weight * n.cx;
		sumY += n.weight * n.cy;
	}
	if(totalWeight <= 0) return nodes;

	const centroidX = sumX / totalWeight;
	const centroidY = sumY / totalWeight;

	const byDistance = nodes
		.map((n) => ({...n, distance: Math.hypot(n.cx - centroidX, n.cy - centroidY)}))
		.sort((a, b) => a.distance - b.distance);

	const targetWeight = totalWeight * keepFraction;
	const kept = [];
	let accumulated = 0;
	for(const n of byDistance){
		kept.push(n);
		accumulated += n.weight;
		if(accumulated >= targetWeight) break;
	}
	return kept;
}

// Returns {min:{x,y,z}, max:{x,y,z}} in the SAME local/offset frame as
// `root.boundingBox` (the caller transforms to world space once, at the
// end - see PointViewTopFraming.js), or null if nothing usable was found
// (no root yet, or every currently-resident node has numPoints === 0 - a
// v1.5+ chunked-hierarchy cloud right after load, before its first .hrc
// chunk has landed, is the one case this can legitimately happen for -
// callers fall back to the point cloud's own already-known bounding box).
//
// `keepFraction` overrides OUTLIER_KEEP_FRACTION - exposed mainly so tests
// can exercise the trimming with a small, easy-to-reason-about node count
// instead of needing hundreds of synthetic nodes to hit a realistic 98%.
export function computeResidentOctreeExtent(root, {keepFraction = OUTLIER_KEEP_FRACTION} = {}){
	if(!root || !root.boundingBox) return null;

	// Collect every resident LEAF node with numPoints > 0 - already fully in
	// memory, see this function's own header comment above for why leaves
	// only. Every node (leaf or not) is still visited to reach its
	// children - only which nodes CONTRIBUTE their box/weight is restricted
	// to leaves.
	const nodes = [];
	const stack = [root];
	while(stack.length > 0){
		const node = stack.pop();
		const children = node.children;
		const childKeys = children ? Object.keys(children) : [];
		const isLeaf = childKeys.length === 0;

		if(isLeaf && node.numPoints > 0 && node.boundingBox){
			const b = node.boundingBox;
			nodes.push({
				box: b,
				weight: node.numPoints,
				cx: (b.min.x + b.max.x) / 2,
				cy: (b.min.y + b.max.y) / 2,
			});
		}

		for(const key of childKeys) stack.push(children[key]);
	}

	if(nodes.length === 0) return null;

	const kept = selectMainMassNodes(nodes, keepFraction);

	let min = null, max = null;
	for(const n of kept){
		const b = n.box;
		if(!min){
			min = {x: b.min.x, y: b.min.y, z: b.min.z};
			max = {x: b.max.x, y: b.max.y, z: b.max.z};
		}else{
			if(b.min.x < min.x) min.x = b.min.x;
			if(b.min.y < min.y) min.y = b.min.y;
			if(b.min.z < min.z) min.z = b.min.z;
			if(b.max.x > max.x) max.x = b.max.x;
			if(b.max.y > max.y) max.y = b.max.y;
			if(b.max.z > max.z) max.z = b.max.z;
		}
	}

	return {min, max};
}

// ── Straight-down camera placement ──────────────────────────────────────
//
// Deliberately NOT the existing 3D bounding-sphere fit (viewer.zoomTo()/
// PerspectiveCamera.prototype.zoomTo - see this feature's own investigation
// notes): that converts the box to a bounding SPHERE via its own diagonal,
// so a box's Z extent (e.g. a pit's actual depth) inflates the camera
// distance even for a straight-down view where Z has no bearing on how
// large the footprint looks from directly above. This computes distance
// from the XY footprint ALONE, against the viewport's own aspect ratio -
// Z only ever decides WHERE (at the extent's own centre) to hang the
// camera above the data, never how FAR.
export const TOP_FRAMING_MARGIN_FACTOR = 1.0; // 0% border (user-requested) - fit the computed extent exactly, no deliberate padding on top of it
const MIN_EXTENT_M = 0.5; // floor so a single point / a degenerate line never yields a zero/negative distance

// distance such that a `width` x `height` rectangle, centred on the camera's
// look-at point, exactly fits inside a perspective frustum of vertical FOV
// `fovDeg` and the given viewport `aspect` (width/height) - the standard
// "fit a rectangle in a frustum" formula, taken from whichever of the two
// dimensions is the binding constraint for this aspect ratio.
export function computeTopFramingDistance({width, height, aspect, fovDeg, marginFactor = TOP_FRAMING_MARGIN_FACTOR}){
	const w = Math.max(width, MIN_EXTENT_M) * marginFactor;
	const h = Math.max(height, MIN_EXTENT_M) * marginFactor;
	const halfFovRad = (fovDeg * Math.PI / 180) / 2;
	const tanHalfFov = Math.tan(halfFovRad);

	const distanceForHeight = h / (2 * tanHalfFov);
	const distanceForWidth = w / (2 * tanHalfFov * aspect);
	return Math.max(distanceForHeight, distanceForWidth);
}

// extent: {min:{x,y,z}, max:{x,y,z}} (world space). Returns
// {target:{x,y,z}, position:{x,y,z}, distance} - position is directly above
// target (same x/y, target.z + distance), matching a straight-down (pitch
// -90°) camera exactly, and target sits at the extent's own XYZ centre
// (matching the existing zoomTo()'s own "centre of the framed volume"
// convention).
export function computeTopFramingPlacement(extent, {aspect, fovDeg, marginFactor = TOP_FRAMING_MARGIN_FACTOR} = {}){
	const width = extent.max.x - extent.min.x;
	const height = extent.max.y - extent.min.y;
	const centerX = (extent.min.x + extent.max.x) / 2;
	const centerY = (extent.min.y + extent.max.y) / 2;
	const centerZ = (extent.min.z + extent.max.z) / 2;

	const distance = computeTopFramingDistance({width, height, aspect, fovDeg, marginFactor});

	return {
		target: {x: centerX, y: centerY, z: centerZ},
		position: {x: centerX, y: centerY, z: centerZ + distance},
		distance,
	};
}
